import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TrialAdminUser } from "../../../../core/models/trial-admin-user.model";
import { SelectOption } from "../../../../core/models/select-option.model";
import { LogHelper } from "../../../../core/helpers/log.helper";
import { AdminService } from "../../../../core/services/admin.service";
import { AlertService } from "../../../../shared/alert/alert.service";
import { ModalComponent } from "../../../../shared/modal/modal.component";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { TrialService } from "../../../../core/services/trial.service";
import { Countries } from "../../../../core/constants/countries";
import { EditTrialAssignmentComponent } from 'app/shared/edit-trial-assignment/edit-trial-assignment.component';
import { AdministratorTrial } from 'app/core/models/administrator-trial.model';
import { UpdateAdministratorTrialAssignmentRequest } from 'app/core/models/admin-user.model';
import { AuthService } from "../../../../core/services/auth.service";
import { RoleNames } from "../../../../core/constants/role-names";

@Component({
  selector: 'app-trial-users',
  templateUrl: './trial-users.component.html',
  styleUrls: ['./trial-users.component.scss']
})
export class TrialUsersComponent implements OnInit, AfterViewInit {
  @ViewChild('assignCmsUserModal') assignCmsUserModal: ModalComponent;
  @ViewChild('unassignCmsUserModal') unassignCmsUserModal: ModalComponent;
  @ViewChild('editTrialAssignment') editTrialAssignment: EditTrialAssignmentComponent;

  @Input('trialId') trialId: string;
  @Input('trialUsers') trialUsers: TrialAdminUser[] = [];

  @Output('refreshTrial') refreshTrial = new EventEmitter();
  @Output('userRoleChanged') userRoleChanged = new EventEmitter<TrialAdminUser>();

  filteredTrialUsers: TrialAdminUser[] = [];

  pageSize = 25;
  currentPage = 1;
  pageCount = 1;
  unassignInProgress = false;

  // Used for assign CMS user dropdown
  allCmsUsers: SelectOption[] = [];
  filteredCmsUsers: SelectOption[] = [];

  assignCmsUserForm: UntypedFormGroup;
  deletingCmsUserId: string;
  deletingCmsUserName: string;
  countryOptions: SelectOption[] = Countries.all().map(c => new SelectOption(c.code, c.name));

  searchForm: UntypedFormGroup;

  constructor(private _adminService: AdminService, private _alertService: AlertService, private _trialService: TrialService, private _authService: AuthService) { }

  ngOnInit(): void {
    this.searchForm = new UntypedFormGroup({
      searchInput: new UntypedFormControl('')
    });

    this.assignCmsUserForm = new UntypedFormGroup({
      processing: new UntypedFormControl(false),
      countries: new UntypedFormControl([]),
      cmsUserId: new UntypedFormControl('', Validators.required),
      projectManager: new UntypedFormControl(false),
      designatedContact: new UntypedFormControl(false),
      endpointDataEditor: new UntypedFormControl(false),
      allCountries: new UntypedFormControl(false)
    });

    this.assignCmsUserForm.get('allCountries').valueChanges.subscribe(allCountries => {
      if (allCountries) {
        this.assignCmsUserForm.get('countries').disable();
      } else {
        this.assignCmsUserForm.get('countries').enable();
      }
    });

    this.loadAvailableCmsUsers();
  }

  ngAfterViewInit() {
    Promise.resolve().then(() => {
      this.filteredTrialUsers = this.trialUsers;
      this.recalculatePageCount();
    });
  }

  shouldShowEndpointDataEditor() {
    return this._authService.hasRole(RoleNames.EndpointDataEditor);
  }

  shouldShowEndpointDataEditorForUser(user: TrialAdminUser) {
    return user.roles.includes('EndpointDataEditor');
  }

  /**
   * Unassigns a CMS user.
   *
   * @param {string} id - The ID of the CMS user to unassign.
   * @param {string} name - The name of the CMS user to unassign.
   * @return {void}
   */
  onUnassignCmsUser(id: string, name: string): void {
    this.deletingCmsUserId = id;
    this.deletingCmsUserName = name;
    this.unassignCmsUserModal.show();
  }

  /**
   * Updates the filteredTrialUsers array based on the provided searchInput.
   *
   * @param {string} searchInput The search input value entered by the user.
   *
   * @return {void}
   */
  onSearchInputChanged(searchInput: string): void {
    LogHelper.log('searchInputChanged ' + searchInput)
    this.filteredTrialUsers = this.trialUsers.filter(u => u.name.toLowerCase().includes(searchInput.toLowerCase()));
  }

  /**
   * Updates the current page to the specified page number.
   *
   * @param {number} page - The page number to be selected.
   * @return {void}
   */
  onPageSelected(page = 1): void {
    this.currentPage = page;
  }

  /**
   * Handles the confirmation of unassigning a CMS user.
   *
   * @returns {void}
   */
  onConfirmUnassignCmsUser(): void {
    this.unassignInProgress = true;

    this._trialService.unassignAdministrator(this.trialId, this.deletingCmsUserId).subscribe({
      next: () => {
        this._alertService.showSuccessAlert('CMS user unassigned successfully!');
        this.trialUsers = this.trialUsers.filter(u => u.id !== this.deletingCmsUserId);
        this.onSearchInputChanged(this.searchForm.controls.searchInput.value);
        this.recalculatePageCount();
        this.unassignCmsUserModal.hide();

        setTimeout(() => {
          this.refreshTrial.emit();
        }, 500);
      },
      error: error => {
        this._alertService.showErrorAlert(error);
      },
      complete: () => {
        this.unassignInProgress = false;
      }
    });
  }

  /**
   * Get CMS users for a specific page.
   *
   * @param {number} page - The page number.
   * @returns {Array} - The CMS users for the given page.
   */
  getCmsUsersForPage(page = 1) {
    const start = (page - 1) * this.pageSize;
    const end = start + this.pageSize;

    return this.filteredTrialUsers.slice(start, end);
  }

  /**
   * Recalculates the total page count based on the number of trial users and page size.
   * @private
   * @returns {void}
   */
  private recalculatePageCount(): void {
    this.pageCount = Math.ceil(this.trialUsers.length / this.pageSize);

    if (this.getCmsUsersForPage(this.currentPage).length === 0) {
      this.currentPage--;

      if (this.currentPage < 1)
        this.currentPage = 1;
    }
  }

  onRoleToggled(administratorId: string, projectManager: boolean, designatedContact: boolean, endpointDataEditor: boolean, allCountries: boolean, countries: string): void {
    this._trialService.assignAdministrator(this.trialId, administratorId, projectManager, designatedContact, endpointDataEditor, allCountries, countries).subscribe({
      next: rsp => {
        this._alertService.clearAll();
        this._alertService.showSuccessAlert('Administrator role updated');
        this.userRoleChanged.emit(new TrialAdminUser({ id: administratorId, projectManager: projectManager, designatedContact: designatedContact, endpointDataEditor: endpointDataEditor }));
      },
      error: err => {
        this._alertService.clearAll();
        this._alertService.showWarningAlert('There was a problem setting the administrator role, please try again!');
      }
    })
  }

  /**
   * Loads the available CMS (Content Management System) users.
   * Calls the 'retrieveAdminUsers' method of the '_adminService' object to get a list of admin users.
   * Populates the 'allCmsUsers' array with objects containing the id and full name of each admin user.
   * Displays a warning alert if there is an error while loading the users.
   *
   * @returns {void}
   */
  loadAvailableCmsUsers(): void {
    this._adminService.retrieveAdminUsers(
      1,
      9999,
      "ProductAdministrator,ProjectCoordinatorExpenses,ProjectCoordinatorTravel,StartupCoordinator,ProjectManager,EndpointDataEditor").subscribe({
        next: results => {
          for (let result of results.results) {
            this.allCmsUsers.push({ value: result.id, text: result.firstname + ' ' + result.lastname });
          }
        }, error: error => {
          LogHelper.log(error);
          this._alertService.showWarningAlert('There was a problem loading coordinators, please try again!');
        }
      });
  }

  /**
   * Resets the assignCmsUserForm and shows the modal window for assigning CMS users.
   *
   * @return {void}
   */
  onShowAssignCmsUserModal() {
    this.assignCmsUserForm.reset();

    // Only show CMS users that are not already assigned to the trial
    this.filteredCmsUsers = this.trialUsers !== undefined && this.trialUsers !== null ? this.allCmsUsers.filter(t => !this.trialUsers.some(u => u.id === t.value)) : this.allCmsUsers;

    // Show the modal window
    this.assignCmsUserModal.show();
  }

  /**
   * Called when the user filters the list of available CMS in the assign CMS user modal window
   * @param searchInput
   */
  filterAssignableCmsUsers(searchInput: string) {
    this.filteredCmsUsers = this.trialUsers !== undefined && this.trialUsers !== null ? this.allCmsUsers.filter(t => !this.trialUsers.some(u => u.id === t.value)) : this.allCmsUsers;

    this.filteredCmsUsers = this.filteredCmsUsers.filter((user) => user.text.toLowerCase().includes(searchInput.toLowerCase()));
  }

  getEditCountriesButtonLabel(user: TrialAdminUser) {
    if (user.allCountries)
      return 'Edit (All)';

    if (!user.countries || user.countries.length === 0)
      return 'Edit (0)';

    const countries = user.countries.split(',');
    if (countries.length > 0)
      return 'Edit (' + countries.length + ')';

    return 'Edit (0)';
  }

  /**
   * Submits the assign form for assigning a CMS user as an administrator to a trial.
   *
   * @return {void}
   */
  onSubmitAssignForm() {
    let form = this.assignCmsUserForm;

    if (form.valid) {
      form.controls.processing.setValue(true);

      this._trialService.assignAdministrator(
        this.trialId,
        form.controls.cmsUserId.value,
        form.controls.projectManager.value,
        form.controls.designatedContact.value,
        form.controls.endpointDataEditor.value,
        form.controls.allCountries.value,
        form.controls.countries.value && form.controls.countries.value.join(',')
      ).subscribe({
        next: (rsp) => {
          this._alertService.showSuccessAlert('CMS user assigned successfully!');
          this.assignCmsUserModal.hide();
          if (this.trialUsers === undefined || this.trialUsers === null)
            this.trialUsers = [];
          this.trialUsers.push(new TrialAdminUser({
            id: rsp.administratorId,
            name: rsp.name,
            email: rsp.email,
            projectManager: rsp.projectManager,
            designatedContact: rsp.designatedContact,
            endpointDataEditor: rsp.endpointDataEditor,
            allCountries: form.controls.allCountries.value,
            countries: form.controls.countries.value && form.controls.countries.value.join(','),
            roles: rsp.roles
          }));
          this.trialUsers.sort((a, b) => a.name.localeCompare(b.name));
          this.recalculatePageCount();

          setTimeout(() => {
            this.refreshTrial.emit();
          }, 500);
        }, error: error => {
          LogHelper.log(error);
          this._alertService.showWarningAlert('There was a problem assigning the user, please try again!');
        }, complete: () => {
          form.controls.processing.setValue(false);
        }
      });
    }
  }

  showEditTrialAssignmentModal(user: TrialAdminUser) {
    this.editTrialAssignment.showEditModal({
      id: user.id,
      code: '',
      projectManager: user.projectManager,
      designatedContact: user.designatedContact,
      endpointDataEditor: user.endpointDataEditor,
      allCountries: user.allCountries,
      assignedCountries: user.countries,
      roles: user.roles
    });
  }

  handleUpdatedAssignment(assignment: AdministratorTrial) {
    let index = this.trialUsers.findIndex(u => u.id === assignment.id);

    this.trialUsers[index].allCountries = assignment.allCountries;
    this.trialUsers[index].countries = assignment.assignedCountries;
    this.trialUsers[index].projectManager = assignment.projectManager;
    this.trialUsers[index].designatedContact = assignment.designatedContact;
    this.trialUsers[index].endpointDataEditor = assignment.endpointDataEditor;

    const updateRequest: UpdateAdministratorTrialAssignmentRequest = {
      id: this.trialUsers[index].assignmentId,
      projectManager: assignment.projectManager,
      designatedContact: assignment.projectManager,
      endpointDataEditor: assignment.endpointDataEditor,
      allCountries: assignment.allCountries,
      assignedCountries: assignment.assignedCountries
    }

    this._adminService.updateTrialAdministratorAssignment(updateRequest).subscribe({
      next: () => {
        this._alertService.showSuccessAlert("Administrator trial assignment successfully updated.");
      },
      error: (err) => {
        LogHelper.log(err);
        this._alertService.showWarningAlert("Failed to update administrator trial assignment. Please try again or contact support.")
      }
    });
  }
}
